"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        {[1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="h-10 w-full animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  return <div className="flex flex-col py-2">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block px-6 py-4 font-default text-lg font-semibold text-foreground hover:bg-accent hover:text-foreground">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <div className="bg-muted/20 pl-4">
              {item.children.map((child, childIdx) => <Link key={child.id} data-index={childIdx} to={child.href || "#"} className="block px-6 py-3 font-default text-base text-foreground/80 hover:text-foreground">
                  {child.label}
                </Link>)}
            </div>}
        </div>)}
    </div>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="222538" id="header" className="sticky top-0 z-50 w-full transition-all duration-300 light bg-background">
      <header className="container mx-auto flex h-24 items-center justify-between px-4 lg:px-8 relative">
        
        <div className="flex items-center gap-8">
          <Link to="/" className="flex shrink-0 items-center">
            <WvcLogo className="h-[45px] w-auto md:h-[55px]" />
          </Link>

          <div className="hidden items-center justify-start md:flex">
            <MenuProvider menu_id="18">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent rounded-lg">
                    <Menu className="size-6" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-80 p-0 bg-background border-border/50 rounded-lg shadow-xl">
                  <div className="p-4 border-b border-border/50">
                    <span className="font-default text-xs font-bold uppercase tracking-widest text-muted-foreground">Navigation</span>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto">
                    <MobileMenuConsumer />
                  </div>
                  <div className="p-6 bg-muted/10 border-t border-border/50">
                    <div className="mb-4 flex flex-col gap-1">
                      <span className="font-default text-sm font-bold text-foreground">Licensed · Insured</span>
                      <span className="font-default text-xs text-muted-foreground">FDACS Licensed Operator</span>
                    </div>
                    <Button variant="default" className="w-full rounded-lg bg-primary py-6 font-default text-sm font-bold uppercase tracking-widest text-primary-foreground" onClick={() => window.location.href = '/#free-inspection'}>
                      Get A Quote
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </MenuProvider>
          </div>
        </div>

        <div className="flex items-center gap-4 lg:gap-8">
          <Link to="tel:941-888-0485" className="hidden md:block font-default text-base font-medium text-foreground hover:text-primary transition-colors md:absolute md:left-1/2 md:-translate-x-1/2" aria-label="Call us at 941-888-0485">
            941-888-0485
          </Link>

          <div className="hidden md:block">
            <Button variant="default" className="h-auto rounded-lg bg-primary px-8 py-3 font-default text-sm font-bold uppercase tracking-widest text-primary-foreground transition-transform hover:scale-105 hover:bg-primary/90" onClick={() => window.location.href = '/#free-inspection'}>
              Learn More
            </Button>
          </div>

          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="flex md:hidden text-foreground" aria-label="Toggle menu">
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] p-0 bg-background">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation Menu</SheetTitle>
              </SheetHeader>
              <div className="flex h-24 items-center justify-between px-6 border-b border-border/50">
                <WvcLogo className="h-[40px] w-auto" />
                <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} className="text-foreground">
                  <X className="size-6" />
                </Button>
              </div>
              <div className="overflow-y-auto h-[calc(100vh-96px)]">
                <MenuProvider menu_id="18">
                  <MobileMenuConsumer />
                </MenuProvider>
                <div className="p-6 mt-auto">
                  <Link to="#">
                    <Button variant="default" className="w-full rounded-lg bg-primary py-6 font-default text-sm font-bold uppercase tracking-widest text-primary-foreground" onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.location.href = '/#free-inspection';
                  }}>
                      Free Inspection
                    </Button>
                  </Link>
                  <Link to="tel:941-888-0485" className="mt-6 block text-center font-default text-lg font-bold text-foreground">
                    941-888-0485
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </section>;
}